import { z } from "zod";
import { useApiQuery, type Api } from "../../../services/api";
import type { SupportedLanguage } from "../../../interfaces";

type SectionsBody =
  Api["get"]["/webapp/public/rooms/:roomId/sections"]["output"]["body"];

/**
 * One entry of the room's Resources order: a section, or a congress space. The
 * server returns them IN ORDER (CX's manual order, else newest first; see
 * `domain/room-order.ts`). Never sort them here.
 */
export type RoomEntry = SectionsBody["entries"][number];
export type RoomSection = Extract<RoomEntry, { kind: "section" }>;
export type RoomCongressEntry = Extract<RoomEntry, { kind: "congress" }>;
export type RoomSectionItem = RoomSection["items"][number];
export type RoomSectionArticleCard = Extract<
  RoomSectionItem,
  { type: "article" }
>["article"];

/** A section with its items in CX's order (`orderedSectionItems`). */
export type ResolvedRoomSection = RoomSection & {
  orderedItems: RoomSectionItem[];
};

// A persisted body is read back from localStorage, a boundary: a bundle built
// before `orderIndex` existed may have stored the body without it.
const withOrderIndex = z.object({ orderIndex: z.number().int() });

/**
 * A section's items in CX's order, for a view of that one section.
 *
 * The server sends `items` in today's order (pinned first, then the newest),
 * which the Resources "All" list merges as it is, and each item's place in
 * CX's order as `orderIndex`. A body persisted before `orderIndex` existed has
 * none: it keeps the order the server sent, today's order, rather than sorting
 * on a field that is not there.
 */
export function orderedSectionItems(
  items: readonly RoomSectionItem[]
): RoomSectionItem[] {
  const ranked = items.flatMap((item) => {
    const parsed = withOrderIndex.safeParse(item);
    return parsed.success ? [{ item, orderIndex: parsed.data.orderIndex }] : [];
  });
  const ordered =
    ranked.length === items.length
      ? ranked
          .sort((a, b) => a.orderIndex - b.orderIndex)
          .map((entry) => entry.item)
      : items;
  // Legacy data can hold one entity twice in a section (the add route now
  // skips an entity already there): it shows once, at its first place.
  const seen = new Set<string>();
  return ordered.filter((item) => {
    const key = entityKeyOf(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function entityKeyOf(item: RoomSectionItem): string {
  switch (item.type) {
    case "article":
      return `article:${item.article.id}`;
    case "content":
      return `content:${item.content.id}`;
    case "playlist":
      return `playlist:${item.playlist.id}`;
    default:
      throw new Error(item satisfies never);
  }
}

type Result =
  | { state: "pending" }
  | { state: "error"; message: string }
  | {
      state: "success";
      /** The room's whole order, sections and congress spaces. */
      entries: RoomEntry[];
      /** The section entries of `entries`, in the same order. */
      sections: ResolvedRoomSection[];
    };

export function useGetRoomSectionsQuery(
  roomId: string,
  language: SupportedLanguage,
  enabled: boolean
): Result {
  const sectionsQuery = useApiQuery(
    "/webapp/public/rooms/:roomId/sections",
    enabled ? { param: { roomId }, query: { language } } : false
  );

  if (sectionsQuery.state === "error") {
    return { state: "error", message: sectionsQuery.message };
  }
  if (sectionsQuery.state !== "success") return { state: "pending" };

  const { entries } = sectionsQuery.body;
  return {
    state: "success",
    entries,
    sections: entries.flatMap((entry) =>
      entry.kind === "section"
        ? [{ ...entry, orderedItems: orderedSectionItems(entry.items) }]
        : []
    ),
  };
}
